import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";

import { AppMetaDataSlice } from "@lxp-app/store/slices/appMetaDataSlice";
import { NotificationApi } from "@lxp-app/components/molecules/notification/slice/notificationSlice";

import SubjectiveSlice from "./slices/subjectiveSlice";
import UserInfoSlice from "@lxp-app/store/slices/userInfoSlice";
import { UserInfoApiSlice } from "@lxp-app/store/slices/api/userInfoApiSlice";

/* admin */
import { QueryArgsSlice } from "@lxp-app/store/slices/query-args/initialState";
import { queryArgsSlice } from "@lxp-app/store/slices/query-args/queryArgsSlice";
import { AdminDashboardApiSlice } from "@lxp-app/pages/admin-dashboard/slices/api/adminDashboardApiSlice";
import { AuthoringViewSlice } from "@lxp-app/pages/authoring-screens/authoringViewSlice";
import { UserUtility } from "@lxp-app/utils/getUser";
import RemarksApi from "@lxp-app/store/slices/api/remarksApi";
import AssessmentAuthSlice from "@lxp-app/pages/assessment-library/slices/assessmentAuthSlice";
import searchQuerySlice from "@lxp-app/store/slices/searchQuerySlice";
import { LearningProgressApi } from "@lxp-app/pages/profile-page/goals-objectives/slices/learningProgressSlice";
import { UtilityUser } from "@lxp-app/pages/agenda-creation/features/api/getUsers";
import BreadcrumbSlice from "@lxp-app/components/molecules/breadcrumb/slices/breadcrumbSlice";
import { QuestionsApi } from "@lxp-app/pages/question-authoring/api/questionsApi";
import SectionListSlice from "@lxp-app/pages/assessment-library/slices/sectionListSlice";
import QuestionStateSlice from "@lxp-app/pages/question-authoring/question-store/question-store-slice";
import NewQuestionStateSlice from "@lxp-app/pages/question-authoring/slice/newQuestionStateSlice";
import QuestionPreviewSlice from "@lxp-app/pages/question-authoring/slice/questionPreviewSlice";
import { CalendarDashboardAdminApi } from "@lxp-app/pages/calendar-dashboard/calendar-dashboard-admin/slices/calendarDashboardAdminApiSlice";
/* Tddo : remove stakeholder from calendrdashboard api and use seperately */

import { fibAssessmentSliceApi } from "@lxp-app/pages/question-authoring/api/fibAssessmentApi";
import { McqAssessmentSliceApi } from "@lxp-app/pages/question-authoring/api/mcqAssessmentApi";
import LearningPathAddNewContentSlice from "@lxp-app/store/slices/authoring/learningPathAddNewContentSlice";
import { SkillsUtility } from "@lxp-app/utils/getSkills";
import { SpeakersUtility } from "@lxp-app/utils/getSpeakers";
import { UtilityApi } from "@lxp-app/pages/create-session/slices/utilitySlice";
import { xapiSlice } from "@lxp-app/components/molecules/analytics/xapiSlice";
import { BulkUploadApi } from "@lxp-app/pages/question-authoring/api/bulkUploadApi";
import { ProctoringDataApi } from "@lxp-app/pages/question-authoring/api/proctoringDataApi";
import { SubjectiveAPI } from "@lxp-app/pages/question-authoring/api/subjectiveAPI";
import { RunCodeApi } from "@lxp-app/pages/question-authoring/api/runCodeApi";
import { CommonControllerApiV2 } from "@lxp-app/pages/question-authoring/api/commonControllerApiV2";
import { aiInterviewApiSlice } from "@ai-interview/store/api/aiInterviewApi";
/* assessment candidate */
import AssessmentSlice from "@assessment/store/slices/assessmentSlice";
import { apiSlice } from "@lxp-app/store/slices/api/apiSlice";
import { AssessmentApiSlice } from "./slices/api/assessmentApiSlice";
import SurveyApi from "@lxp-app/components/molecules/surveyjs/survey-slice/surveySlice";
import CommonConfigApi from "@lxp-app/store/slices/api/commonConfigApi";
import DarkmodeSlice from "@lxp-app/store/slices/darkmodeSlice";
import { ActionButtonSlice } from "@lxp-app/components/atoms/content-authoring-tabular/actionButtonSlice";
import { UploadGetApi } from "@lxp-app/pages/eventCreationWorkflowV2/slices/uploadGetApi";
import AssessmentReportSlice from "@lxp-app/pages/question-authoring/candidateResultSummaryForAuthoring/slices/assessmentReportSlice";
import { GetUserBySearchKey } from "@lxp-app/store/slices/api/getUserBySearchkey";
import { DetailedAssessmentResultsApi } from "@lxp-app/pages/question-authoring/api/detailedAssessmentResultsApi";
import { UserGroupAuthoringApi } from "@lxp-app/pages/group-authoring/api/userGroupAuthoringApis";
import { ManageRoleDashboardAPI } from "@lxp-app/pages/manage-role/manageRoleDashboard/manageRoleDashboardAPI";
import { AssignEditRoleSLice } from "@lxp-app/pages/manage-role/assignAndEditRoles/assignEditRoleSlice";
import { AssignRoleAPISlice } from "@lxp-app/pages/manage-role/assignAndEditRoles/assignRoleAPI";
import UserGroupSlice from "@lxp-app/pages/group-authoring/slices/userGroupSlice";

const reducer = combineReducers({
  [AppMetaDataSlice.name]: AppMetaDataSlice.reducer,
  [NotificationApi.reducerPath]: NotificationApi.reducer,
  [RemarksApi.reducerPath]: RemarksApi.reducer,
  [queryArgsSlice.name]: queryArgsSlice.reducer,
  [DarkmodeSlice.name]: DarkmodeSlice.reducer,
  [AssessmentSlice.name]: AssessmentSlice.reducer,
  searchQuery: searchQuerySlice,
  [SubjectiveSlice.name]: SubjectiveSlice.reducer,
  [AssessmentApiSlice.reducerPath]: AssessmentApiSlice.reducer,
  [UserInfoSlice.name]: UserInfoSlice.reducer,
  [UserUtility.reducerPath]: UserUtility.reducer,
  [UserInfoApiSlice.reducerPath]: UserInfoApiSlice.reducer,
  [LearningProgressApi.reducerPath]: LearningProgressApi.reducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
  [AdminDashboardApiSlice.reducerPath]: AdminDashboardApiSlice.reducer,
  [SurveyApi.reducerPath]: SurveyApi.reducer,
  [AssessmentAuthSlice.reducerPath]: AssessmentAuthSlice.reducer,
  [CommonConfigApi.reducerPath]: CommonConfigApi.reducer,
  [ActionButtonSlice.reducerPath]: ActionButtonSlice.reducer,
  [AuthoringViewSlice.reducerPath]: AuthoringViewSlice.reducer,
  [CalendarDashboardAdminApi.reducerPath]: CalendarDashboardAdminApi.reducer,
  [UploadGetApi.reducerPath]: UploadGetApi.reducer,
  [UtilityUser.reducerPath]: UtilityUser.reducer,
  [SectionListSlice.reducerPath]: SectionListSlice.reducer,
  [QuestionStateSlice.reducerPath]: QuestionStateSlice.reducer,
  [NewQuestionStateSlice.reducerPath]: NewQuestionStateSlice.reducer,
  [QuestionPreviewSlice.reducerPath]: QuestionPreviewSlice.reducer,
  [QuestionsApi.reducerPath]: QuestionsApi.reducer,
  [McqAssessmentSliceApi.reducerPath]: McqAssessmentSliceApi.reducer,
  [fibAssessmentSliceApi.reducerPath]: fibAssessmentSliceApi.reducer,
  [LearningPathAddNewContentSlice.reducerPath]:
    LearningPathAddNewContentSlice.reducer,
  [SpeakersUtility.reducerPath]: SpeakersUtility.reducer,
  [SkillsUtility.reducerPath]: SkillsUtility.reducer,
  [UtilityApi.reducerPath]: UtilityApi.reducer,
  [xapiSlice.name]: xapiSlice.reducer,

  breadCrumb: BreadcrumbSlice.reducer,
  assessmentReportSlice: AssessmentReportSlice.reducer,
  [BulkUploadApi.reducerPath]: BulkUploadApi.reducer,
  [RunCodeApi.reducerPath]: RunCodeApi.reducer,
  [ProctoringDataApi.reducerPath]: ProctoringDataApi.reducer,
  [SubjectiveAPI.reducerPath]: SubjectiveAPI.reducer,
  [CommonControllerApiV2.reducerPath]: CommonControllerApiV2.reducer,
  [aiInterviewApiSlice.reducerPath]: aiInterviewApiSlice.reducer,
  [GetUserBySearchKey.reducerPath]: GetUserBySearchKey.reducer,
  [DetailedAssessmentResultsApi.reducerPath]:
    DetailedAssessmentResultsApi.reducer,
  [UserGroupAuthoringApi.reducerPath]: UserGroupAuthoringApi.reducer,
  [UserGroupSlice.reducerPath]: UserGroupSlice.reducer,
  [ManageRoleDashboardAPI.reducerPath]: ManageRoleDashboardAPI.reducer,
  [AssignRoleAPISlice.reducerPath]: AssignRoleAPISlice.reducer,
  [AssignEditRoleSLice.reducerPath]: AssignEditRoleSLice.reducer
});

export const store = configureStore({
  reducer,
  devTools: true,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().prepend(
      UserInfoApiSlice.middleware,
      NotificationApi.middleware,
      CommonConfigApi.middleware,
      UtilityApi.middleware,
      CalendarDashboardAdminApi.middleware,
      apiSlice.middleware,
      AdminDashboardApiSlice.middleware,
      AssessmentApiSlice.middleware,
      SurveyApi.middleware,
      UserUtility.middleware,
      RemarksApi.middleware,
      UploadGetApi.middleware,
      LearningProgressApi.middleware,
      QuestionsApi.middleware,
      McqAssessmentSliceApi.middleware,
      fibAssessmentSliceApi.middleware,
      SpeakersUtility.middleware,
      SkillsUtility.middleware,
      UtilityUser.middleware,
      BulkUploadApi.middleware,
      ProctoringDataApi.middleware,
      RunCodeApi.middleware,
      SubjectiveAPI.middleware,
      CommonControllerApiV2.middleware,
      aiInterviewApiSlice.middleware,
      GetUserBySearchKey.middleware,
      DetailedAssessmentResultsApi.middleware,
      UserGroupAuthoringApi.middleware,
      ManageRoleDashboardAPI.middleware,
      AssignRoleAPISlice.middleware
    )
});

export type RootState = ReturnType<typeof store.getState> & {
  queryArgsSlice: QueryArgsSlice;
};
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = useDispatch<AppDispatch>;
